import React, { useEffect, useState } from "react";
import { TableBody, TableCell, TableHead, TableRow } from "@material-ui/core";
import { useNavigate } from "react-router-dom";
import {
  ActivityIndicator,
  Dimensions,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native-web";
import { COLORS } from "constants/Colors";
import { PrimaryText } from "components/Common/PrimaryText";
import FormInput from "components/Inputs/FormInput";
import * as FaIcons from "react-icons/fa";
import { useCampaigns } from "context/CampaignsContext";
import { useDisplay } from "context/DisplayContext";
import { Links } from "constants/Links";
import Table from "components/Common/TableComponent/Table/Table";
import { FullModal } from "components/Modals/FullModal";
import moment from "moment";
import { GenerateQRModal } from "../../GenerateQRModal/GenerateQRModal";
import { uploadLogo, generateLogoPath } from "services/storage-service";

const { height } = Dimensions.get("window");
const pageSize = 10;

export const CampaignsList = () => {
  const navigate = useNavigate();
  const { isMobile } = useDisplay();
  const { campaigns, loading, fetchCampaigns, selectedCampaign, setSelectedCampaign, toggleStatus, createCampaign, fetchStats, fetchClaims, campaignStats, claims, deleteCampaign } = useCampaigns();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(campaigns);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [detailView, setDetailView] = useState("overview"); // overview or claims
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    promoCode: "",
    promoLabel: "",
    storeName: "",
    storeAddress: "",
    disclaimer: "",
    startsAt: "",
    endsAt: "",
    logoUrl: "",
    storeLogoUrl: "",
  });
  const [errors, setErrors] = useState({});
  const [formLoading, setFormLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingStoreLogo, setUploadingStoreLogo] = useState(false);
  const [foreverxLogoFile, setForeverxLogoFile] = useState(null);
  const [storeLogoFile, setStoreLogoFile] = useState(null);
  const [foreverxLogoPreview, setForeverxLogoPreview] = useState(null);
  const [storeLogoPreview, setStoreLogoPreview] = useState(null);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  useEffect(() => {
    if (search) {
      const filtered = campaigns.filter(
        (campaign) =>
          campaign.name?.toLowerCase().includes(search.toLowerCase()) ||
          campaign.id?.toLowerCase().includes(search.toLowerCase()) ||
          campaign.storeName?.toLowerCase().includes(search.toLowerCase())
      );
      setRows(filtered);
      setPage(1);
    } else {
      setRows(campaigns);
    }
  }, [search, campaigns]);

  useEffect(() => {
    if (showDetailModal && selectedCampaign) {
      fetchStats(selectedCampaign.id);
      fetchClaims(selectedCampaign.id);
    }
  }, [showDetailModal, selectedCampaign]);

  const handleCampaignClick = (campaign) => {
    setSelectedCampaign(campaign);
    setShowDetailModal(true);
    setDetailView("overview");
  };

  const handleCreateCampaign = () => {
    setShowCreateModal(true);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.id.trim()) newErrors.id = "Campaign ID is required";
    if (!formData.name.trim()) newErrors.name = "Campaign name is required";
    if (!formData.promoCode.trim()) newErrors.promoCode = "Promo code is required";
    if (!formData.promoLabel.trim()) newErrors.promoLabel = "Promo label is required";
    if (!formData.storeName.trim()) newErrors.storeName = "Store name is required";
    if (!formData.storeAddress.trim()) newErrors.storeAddress = "Store address is required";
    if (!formData.startsAt) newErrors.startsAt = "Start date is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogoSelect = (file, isStoreLogo = false) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      alert("Invalid file type. Please upload a JPG, PNG, or WebP image.");
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert("File size exceeds 5MB limit.");
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      if (isStoreLogo) {
        setStoreLogoFile(file);
        setStoreLogoPreview(reader.result);
      } else {
        setForeverxLogoFile(file);
        setForeverxLogoPreview(reader.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setFormLoading(true);

    try {
      // Upload logos if selected
      let logoUrl = formData.logoUrl;
      let storeLogoUrl = formData.storeLogoUrl;

      if (foreverxLogoFile) {
        setUploadingLogo(true);
        const logoPath = generateLogoPath(formData.id, "foreverx");
        logoUrl = await uploadLogo(foreverxLogoFile, logoPath);
        setUploadingLogo(false);
      }

      if (storeLogoFile) {
        setUploadingStoreLogo(true);
        const storeLogoPath = generateLogoPath(formData.id, "store");
        storeLogoUrl = await uploadLogo(storeLogoFile, storeLogoPath);
        setUploadingStoreLogo(false);
      }

      // Create campaign with logo URLs
      await createCampaign({
        ...formData,
        logoUrl,
        storeLogoUrl,
        startsAt: new Date(formData.startsAt).toISOString(),
        endsAt: formData.endsAt ? new Date(formData.endsAt).toISOString() : null,
      });

      // Reset form
      setFormData({
        id: "",
        name: "",
        promoCode: "",
        promoLabel: "",
        storeName: "",
        storeAddress: "",
        disclaimer: "",
        startsAt: "",
        endsAt: "",
        logoUrl: "",
        storeLogoUrl: "",
      });
      setForeverxLogoFile(null);
      setStoreLogoFile(null);
      setForeverxLogoPreview(null);
      setStoreLogoPreview(null);
      setErrors({});
      setShowCreateModal(false);
    } catch (error) {
      alert(`Error creating campaign: ${error.message}`);
    } finally {
      setFormLoading(false);
      setUploadingLogo(false);
      setUploadingStoreLogo(false);
    }
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleGenerateQR = (campaign, e) => {
    e.stopPropagation();
    setSelectedCampaign(campaign);
    setShowQRModal(true);
  };

  const handleDetailToggleStatus = async () => {
    if (!selectedCampaign) return;

    const confirm = window.confirm(
      `Are you sure you want to ${selectedCampaign.isActive ? "deactivate" : "activate"} this campaign?`
    );
    if (confirm) {
      try {
        await toggleStatus(selectedCampaign.id, !selectedCampaign.isActive);
      } catch (error) {
        alert(`Error: ${error.message}`);
      }
    }
  };

  const handleDetailDelete = async () => {
    if (!selectedCampaign) return;

    const confirm = window.confirm(
      "Are you sure you want to delete this campaign? This action cannot be undone."
    );
    if (confirm) {
      try {
        await deleteCampaign(selectedCampaign.id);
        setShowDetailModal(false);
      } catch (error) {
        alert(`Error: ${error.message}`);
      }
    }
  };

  const handleToggleStatus = async (campaign, e) => {
    e.stopPropagation();
    const confirm = window.confirm(
      `Are you sure you want to ${campaign.isActive ? "deactivate" : "activate"} this campaign?`
    );
    if (confirm) {
      try {
        await toggleStatus(campaign.id, !campaign.isActive);
      } catch (error) {
        alert(`Error: ${error.message}`);
      }
    }
  };

  const isExpired = (campaign) => campaign.endsAt && new Date(campaign.endsAt) < new Date();
  const isNotStarted = (campaign) => campaign.startsAt && new Date(campaign.startsAt) > new Date();
  const isActive = (campaign) => campaign.isActive && !isExpired(campaign) && !isNotStarted(campaign);


  const currentTableData = () => {
    const firstPageIndex = (page - 1) * pageSize;
    const lastPageIndex = firstPageIndex + pageSize;
    return rows.slice(firstPageIndex, lastPageIndex);
  };

  const totalCount = rows?.length || 0;
  const totalPageCount = Math.ceil(totalCount / pageSize);
  const startCount = (page - 1) * pageSize + 1;
  const endCount = startCount + currentTableData()?.length - 1;

  if (loading && campaigns.length === 0) {
    return (
      <View
        style={{
          height: height - 100,
          width: "100%",
          flexDirection: "column",
          backgroundColor: COLORS.primary.white,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <ActivityIndicator
          animating={loading}
          size={"large"}
          style={StyleSheet.absoluteFill}
          color={COLORS.primary.blue}
        />
      </View>
    );
  }

  return (
    <View style={styles.tableContainer}>
      <View
        style={{
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "stretch" : "center",
          paddingHorizontal: isMobile ? 12 : 24,
          marginTop: 12,
          gap: isMobile ? 8 : 0,
        }}
      >
        <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
          <PrimaryText fontSize={isMobile ? 24 : 32} fontWeight={800}>
            Campaigns
          </PrimaryText>
          {isMobile && (
            <TouchableOpacity
              onPress={handleCreateCampaign}
              style={{
                flexDirection: "row",
                backgroundColor: COLORS.primary.blue,
                paddingVertical: 8,
                paddingHorizontal: 12,
                borderRadius: 8,
                alignItems: "center",
                minHeight: 44,
                minWidth: 44,
                justifyContent: "center",
              }}
            >
              <FaIcons.FaPlus size={14} color={COLORS.primary.white} />
            </TouchableOpacity>
          )}
        </View>

        <View style={{ flexDirection: "row", alignItems: "center", flex: isMobile ? undefined : 2 }}>
          <FaIcons.FaSearch size={16} color={COLORS.primary.darkGray} />
          <div className="flex-1" style={{ marginHorizontal: 8, flex: 1 }}>
            <FormInput
              type="search"
              placeholder="Search campaigns..."
              value={search}
              setValue={setSearch}
            />
          </div>
        </View>

        {!isMobile && (
          <TouchableOpacity
            onPress={handleCreateCampaign}
            style={{
              flexDirection: "row",
              backgroundColor: COLORS.primary.blue,
              paddingVertical: 10,
              paddingHorizontal: 20,
              borderRadius: 8,
              alignItems: "center",
              marginLeft: 12,
              minHeight: 44,
            }}
          >
            <FaIcons.FaPlus size={16} color={COLORS.primary.white} />
            <PrimaryText
              fontSize={16}
              fontWeight={600}
              color={COLORS.primary.white}
              style={{ marginLeft: 8 }}
            >
              Create Campaign
            </PrimaryText>
          </TouchableOpacity>
        )}
      </View>

      {currentTableData() && currentTableData().length > 0 && (
        <div style={{ width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <PrimaryText fontSize={24} fontWeight={800}>
                    ID
                  </PrimaryText>
                </TableCell>
                <TableCell>
                  <PrimaryText fontSize={24} fontWeight={800}>
                    Name
                  </PrimaryText>
                </TableCell>
                <TableCell>
                  <PrimaryText fontSize={24} fontWeight={800}>
                    Store
                  </PrimaryText>
                </TableCell>
                <TableCell>
                  <PrimaryText fontSize={24} fontWeight={800}>
                    Promo Code
                  </PrimaryText>
                </TableCell>
                <TableCell>
                  <PrimaryText fontSize={24} fontWeight={800}>
                    Label
                  </PrimaryText>
                </TableCell>
                <TableCell>
                  <PrimaryText fontSize={24} fontWeight={800}>
                    Duration
                  </PrimaryText>
                </TableCell>
                <TableCell>
                  <PrimaryText fontSize={24} fontWeight={800}>
                    Status
                  </PrimaryText>
                </TableCell>
                <TableCell>
                  <PrimaryText fontSize={24} fontWeight={800}>
                    Created
                  </PrimaryText>
                </TableCell>
                <TableCell>
                  <PrimaryText fontSize={24} fontWeight={800}>
                    Actions
                  </PrimaryText>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentTableData().map((campaign) => {
                const isExpired = campaign.endsAt && new Date(campaign.endsAt) < new Date();
                const isNotStarted = campaign.startsAt && new Date(campaign.startsAt) > new Date();
                const isActive = campaign.isActive && !isExpired && !isNotStarted;

                return (
                  <TableRow
                    hover
                    key={campaign.id}
                    onClick={() => handleCampaignClick(campaign)}
                  >
                    <TableCell>
                      <PrimaryText fontSize={14} fontWeight={400}>
                        {campaign.id}
                      </PrimaryText>
                    </TableCell>
                    <TableCell>
                      <PrimaryText fontSize={14} fontWeight={600}>
                        {campaign.name}
                      </PrimaryText>
                    </TableCell>
                    <TableCell>
                      <PrimaryText fontSize={14} fontWeight={400}>
                        {campaign.storeName}
                      </PrimaryText>
                    </TableCell>
                    <TableCell>
                      <PrimaryText fontSize={14} fontWeight={600} style={{ color: COLORS.primary.blue }}>
                        {campaign.promoCode}
                      </PrimaryText>
                    </TableCell>
                    <TableCell>
                      <PrimaryText fontSize={14} fontWeight={400}>
                        {campaign.promoLabel}
                      </PrimaryText>
                    </TableCell>
                    <TableCell>
                      <PrimaryText fontSize={12} fontWeight={400}>
                        {moment(campaign.startsAt).format("MMM DD, YYYY")}
                        {" - "}
                        {campaign.endsAt
                          ? moment(campaign.endsAt).format("MMM DD, YYYY")
                          : "Ongoing"}
                      </PrimaryText>
                    </TableCell>
                    <TableCell>
                      <View
                        style={{
                          backgroundColor: isActive
                            ? "#E8F5E9"
                            : isExpired
                            ? "#FFEBEE"
                            : isNotStarted
                            ? "#FFF3E0"
                            : "#F5F5F5",
                          paddingVertical: 4,
                          paddingHorizontal: 12,
                          borderRadius: 12,
                        }}
                      >
                        <PrimaryText
                          fontSize={12}
                          fontWeight={600}
                          color={
                            isActive
                              ? "#2E7D32"
                              : isExpired
                              ? "#C62828"
                              : isNotStarted
                              ? "#EF6C00"
                              : "#757575"
                          }
                        >
                          {isActive ? "Active" : isExpired ? "Ended" : isNotStarted ? "Upcoming" : "Inactive"}
                        </PrimaryText>
                      </View>
                    </TableCell>
                    <TableCell>
                      <PrimaryText fontSize={14} fontWeight={400}>
                        {campaign.createdAt
                          ? moment(campaign.createdAt).format("MMM DD, YYYY")
                          : ""}
                      </PrimaryText>
                    </TableCell>
                    <TableCell>
                      <View style={{ flexDirection: "row", gap: 8 }}>
                        <TouchableOpacity
                          onPress={(e) => handleGenerateQR(campaign, e)}
                          style={{ padding: 4, minHeight: 44, minWidth: 44, justifyContent: "center", alignItems: "center" }}
                        >
                          <FaIcons.FaQrcode size={16} color={COLORS.primary.darkGray} />
                        </TouchableOpacity>
                        <TouchableOpacity
                          onPress={(e) => handleToggleStatus(campaign, e)}
                          style={{ padding: 4, minHeight: 44, minWidth: 44, justifyContent: "center", alignItems: "center" }}
                        >
                          <FaIcons.FaPowerOff
                            size={16}
                            color={isActive ? COLORS.primary.error : COLORS.primary.blue}
                          />
                        </TouchableOpacity>
                      </View>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {!loading && currentTableData().length === 0 && (
        <View
          style={{
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            paddingVertical: 60,
          }}
        >
          <PrimaryText fontSize={18} fontWeight={500} style={{ color: COLORS.primary.gray }}>
            {search ? "No campaigns found matching your search." : "No campaigns yet."}
          </PrimaryText>
          {!search && (
            <TouchableOpacity
              onPress={handleCreateCampaign}
              style={{
                flexDirection: "row",
                backgroundColor: COLORS.primary.blue,
                paddingVertical: 12,
                paddingHorizontal: 24,
                borderRadius: 8,
                alignItems: "center",
                marginTop: 16,
                minHeight: 44,
              }}
            >
              <PrimaryText fontSize={16} fontWeight={600} style={{ color: COLORS.primary.white }}>
                Create Your First Campaign
              </PrimaryText>
            </TouchableOpacity>
          )}
        </View>
      )}

      {totalCount > pageSize && (
        <View style={{ flexDirection: "row", alignSelf: "flex-end", marginTop: 12 }}>
          <TouchableOpacity onPress={() => page > 1 && setPage(page - 1)}>
            <FaIcons.FaChevronLeft size={20} color={COLORS.primary.darkGray} />
          </TouchableOpacity>

          <View style={{ width: 125, alignItems: "center" }}>
            <PrimaryText fontSize={14} fontWeight={400}>
              {`${startCount} - ${endCount} of ${totalCount}`}
            </PrimaryText>
          </View>

          <TouchableOpacity onPress={() => page < totalPageCount && setPage(page + 1)}>
            <FaIcons.FaChevronRight size={20} color={COLORS.primary.darkGray} />
          </TouchableOpacity>
        </View>
      )}

      {/* Create Campaign Modal */}
      {showCreateModal && (
        <FullModal setShowModal={() => setShowCreateModal(false)} title="Create New Campaign">
          <ScrollView style={{ paddingHorizontal: isMobile ? 12 : 20 }}>
            {/* Campaign ID */}
            <View style={{ marginBottom: 20 }}>
              <PrimaryText fontSize={16} fontWeight={600} style={{ marginBottom: 4 }}>
                Campaign ID *
              </PrimaryText>
              <PrimaryText fontSize={12} style={{ color: COLORS.primary.gray, marginBottom: 8 }}>
                Unique identifier (e.g., NYC_DOWNTOWN)
              </PrimaryText>
              <FormInput
                type="text"
                placeholder="e.g., NYC_DOWNTOWN"
                value={formData.id}
                setValue={(value) => updateField("id", value)}
              />
            </View>

            {/* Campaign Name */}
            <View style={{ marginBottom: 20 }}>
              <PrimaryText fontSize={16} fontWeight={600} style={{ marginBottom: 4 }}>
                Campaign Name *
              </PrimaryText>
              <FormInput
                type="text"
                placeholder="e.g., ForeverX NYC - Downtown"
                value={formData.name}
                setValue={(value) => updateField("name", value)}
              />
            </View>

            {/* Promo Code */}
            <View style={{ marginBottom: 20 }}>
              <PrimaryText fontSize={16} fontWeight={600} style={{ marginBottom: 4 }}>
                Static Promo Code *
              </PrimaryText>
              <PrimaryText fontSize={12} style={{ color: COLORS.primary.gray, marginBottom: 8 }}>
                The code shown to staff (e.g., FOREVERX10)
              </PrimaryText>
              <FormInput
                type="text"
                placeholder="e.g., FOREVERX10"
                value={formData.promoCode}
                setValue={(value) => updateField("promoCode", value)}
              />
            </View>

            {/* Promo Label */}
            <View style={{ marginBottom: 20 }}>
              <PrimaryText fontSize={16} fontWeight={600} style={{ marginBottom: 4 }}>
                Promo Label *
              </PrimaryText>
              <PrimaryText fontSize={12} style={{ color: COLORS.primary.gray, marginBottom: 8 }}>
                Discount description shown to users
              </PrimaryText>
              <FormInput
                type="text"
                placeholder="e.g., 10% OFF"
                value={formData.promoLabel}
                setValue={(value) => updateField("promoLabel", value)}
              />
            </View>

            {/* Store Name */}
            <View style={{ marginBottom: 20 }}>
              <PrimaryText fontSize={16} fontWeight={600} style={{ marginBottom: 4 }}>
                Store Name *
              </PrimaryText>
              <FormInput
                type="text"
                placeholder="e.g., ForeverX NYC - Downtown"
                value={formData.storeName}
                setValue={(value) => updateField("storeName", value)}
              />
            </View>

            {/* Store Address */}
            <View style={{ marginBottom: 20 }}>
              <PrimaryText fontSize={16} fontWeight={600} style={{ marginBottom: 4 }}>
                Store Address *
              </PrimaryText>
              <FormInput
                type="text"
                placeholder="e.g., 123 Main St, New York, NY 10001"
                value={formData.storeAddress}
                setValue={(value) => updateField("storeAddress", value)}
              />
            </View>

            {/* Disclaimer */}
            <View style={{ marginBottom: 20 }}>
              <PrimaryText fontSize={16} fontWeight={600} style={{ marginBottom: 4 }}>
                Disclaimer
              </PrimaryText>
              <PrimaryText fontSize={12} style={{ color: COLORS.primary.gray, marginBottom: 8 }}>
                Terms and conditions shown to users
              </PrimaryText>
              <FormInput
                type="text"
                placeholder="e.g., Valid only at this location. One per customer."
                value={formData.disclaimer}
                setValue={(value) => updateField("disclaimer", value)}
              />
            </View>

            {/* ForeveRx Logo Upload */}
            <View style={{ marginBottom: 20 }}>
              <PrimaryText fontSize={16} fontWeight={600} style={{ marginBottom: 4 }}>
                ForeveRx Logo
              </PrimaryText>
              <PrimaryText fontSize={12} style={{ color: COLORS.primary.gray, marginBottom: 8 }}>
                Upload the ForeveRx branding logo (optional)
              </PrimaryText>

              {foreverxLogoPreview ? (
                <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
                  <img
                    src={foreverxLogoPreview}
                    alt="ForeveRx Logo Preview"
                    style={{ width: 80, height: 80, objectFit: "contain", borderRadius: 8, border: "1px solid #E0E0E0" }}
                  />
                  <View style={{ flex: 1 }}>
                    <PrimaryText fontSize={13} fontWeight={500}>
                      {foreverxLogoFile?.name}
                    </PrimaryText>
                    <PrimaryText fontSize={12} style={{ color: COLORS.primary.gray }}>
                      {(foreverxLogoFile?.size / 1024).toFixed(1)} KB
                    </PrimaryText>
                    <TouchableOpacity
                      onPress={() => {
                        setForeverxLogoFile(null);
                        setForeverxLogoPreview(null);
                      }}
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 4,
                        marginTop: 8,
                      }}
                    >
                      <FaIcons.FaTimes size={12} color={COLORS.primary.error} />
                      <PrimaryText fontSize={12} style={{ color: COLORS.primary.error }}>
                        Remove
                      </PrimaryText>
                    </TouchableOpacity>
                  </View>
                </View>
              ) : (
                <View>
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleLogoSelect(file, false);
                    }}
                    style={{ display: "none" }}
                    id="foreverx-logo-input"
                  />
                  <TouchableOpacity
                    onPress={() => document.getElementById("foreverx-logo-input").click()}
                    disabled={uploadingLogo}
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      paddingVertical: 12,
                      paddingHorizontal: 16,
                      borderWidth: 2,
                      borderColor: COLORS.primary.lightGray,
                      borderStyle: "dashed",
                      borderRadius: 8,
                      backgroundColor: COLORS.primary.white,
                      minHeight: 44,
                    }}
                  >
                    {uploadingLogo ? (
                      <>
                        <ActivityIndicator size="small" color={COLORS.primary.blue} />
                        <PrimaryText fontSize={14} style={{ color: COLORS.primary.gray }}>
                          Uploading...
                        </PrimaryText>
                      </>
                    ) : (
                      <>
                        <FaIcons.FaCloudUploadAlt size={18} color={COLORS.primary.blue} />
                        <PrimaryText fontSize={14} style={{ color: COLORS.primary.blue }}>
                          Choose Image
                        </PrimaryText>
                      </>
                    )}
                  </TouchableOpacity>
                </View>
              )}
            </View>

            {/* Store Logo Upload */}
            <View style={{ marginBottom: 20 }}>
              <PrimaryText fontSize={16} fontWeight={600} style={{ marginBottom: 4 }}>
                Store Logo
              </PrimaryText>
              <PrimaryText fontSize={12} style={{ color: COLORS.primary.gray, marginBottom: 8 }}>
                Upload the restaurant/store logo (optional)
              </PrimaryText>

              {storeLogoPreview ? (
                <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
                  <img
                    src={storeLogoPreview}
                    alt="Store Logo Preview"
                    style={{ width: 80, height: 80, objectFit: "contain", borderRadius: 8, border: "1px solid #E0E0E0" }}
                  />
                  <View style={{ flex: 1 }}>
                    <PrimaryText fontSize={13} fontWeight={500}>
                      {storeLogoFile?.name}
                    </PrimaryText>
                    <PrimaryText fontSize={12} style={{ color: COLORS.primary.gray }}>
                      {(storeLogoFile?.size / 1024).toFixed(1)} KB
                    </PrimaryText>
                    <TouchableOpacity
                      onPress={() => {
                        setStoreLogoFile(null);
                        setStoreLogoPreview(null);
                      }}
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 4,
                        marginTop: 8,
                      }}
                    >
                      <FaIcons.FaTimes size={12} color={COLORS.primary.error} />
                      <PrimaryText fontSize={12} style={{ color: COLORS.primary.error }}>
                        Remove
                      </PrimaryText>
                    </TouchableOpacity>
                  </View>
                </View>
              ) : (
                <View>
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) handleLogoSelect(file, true);
                    }}
                    style={{ display: "none" }}
                    id="store-logo-input"
                  />
                  <TouchableOpacity
                    onPress={() => document.getElementById("store-logo-input").click()}
                    disabled={uploadingStoreLogo}
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      paddingVertical: 12,
                      paddingHorizontal: 16,
                      borderWidth: 2,
                      borderColor: COLORS.primary.lightGray,
                      borderStyle: "dashed",
                      borderRadius: 8,
                      backgroundColor: COLORS.primary.white,
                      minHeight: 44,
                    }}
                  >
                    {uploadingStoreLogo ? (
                      <>
                        <ActivityIndicator size="small" color={COLORS.primary.blue} />
                        <PrimaryText fontSize={14} style={{ color: COLORS.primary.gray }}>
                          Uploading...
                        </PrimaryText>
                      </>
                    ) : (
                      <>
                        <FaIcons.FaCloudUploadAlt size={18} color={COLORS.primary.blue} />
                        <PrimaryText fontSize={14} style={{ color: COLORS.primary.blue }}>
                          Choose Image
                        </PrimaryText>
                      </>
                    )}
                  </TouchableOpacity>
                </View>
              )}
            </View>

            {/* Start Date/Time */}
            <View style={{ marginBottom: 20 }}>
              <PrimaryText fontSize={16} fontWeight={600} style={{ marginBottom: 4 }}>
                Start Date & Time *
              </PrimaryText>
              <PrimaryText fontSize={12} style={{ color: COLORS.primary.gray, marginBottom: 8 }}>
                When the campaign becomes active
              </PrimaryText>
              <input
                type="datetime-local"
                value={formData.startsAt}
                onChange={(e) => updateField("startsAt", e.target.value)}
                style={{
                  width: "100%",
                  height: 48,
                  borderWidth: 1,
                  borderColor: "#E0E0E0",
                  borderRadius: 8,
                  paddingHorizontal: 12,
                  fontSize: 14,
                  fontFamily: "Montserrat-Regular",
                }}
              />
            </View>

            {/* End Date/Time */}
            <View style={{ marginBottom: 20 }}>
              <PrimaryText fontSize={16} fontWeight={600} style={{ marginBottom: 4 }}>
                End Date & Time
              </PrimaryText>
              <PrimaryText fontSize={12} style={{ color: COLORS.primary.gray, marginBottom: 8 }}>
                Leave empty for ongoing campaign
              </PrimaryText>
              <input
                type="datetime-local"
                value={formData.endsAt}
                onChange={(e) => updateField("endsAt", e.target.value)}
                min={formData.startsAt}
                style={{
                  width: "100%",
                  height: 48,
                  borderWidth: 1,
                  borderColor: "#E0E0E0",
                  borderRadius: 8,
                  paddingHorizontal: 12,
                  fontSize: 14,
                  fontFamily: "Montserrat-Regular",
                }}
              />
            </View>

            {/* Action Buttons */}
            <View style={{ flexDirection: "row", gap: 12, marginTop: 20, marginBottom: 20 }}>
              <TouchableOpacity
                onPress={() => setShowCreateModal(false)}
                style={{
                  flex: 1,
                  paddingVertical: 14,
                  borderRadius: 8,
                  alignItems: "center",
                  backgroundColor: COLORS.primary.lightGray,
                  minHeight: 44,
                }}
                disabled={formLoading}
              >
                <PrimaryText fontSize={16} fontWeight={600} style={{ color: COLORS.primary.darkGray }}>
                  Cancel
                </PrimaryText>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={handleSubmit}
                style={{
                  flex: 1,
                  paddingVertical: 14,
                  borderRadius: 8,
                  alignItems: "center",
                  backgroundColor: COLORS.primary.blue,
                  minHeight: 44,
                }}
                disabled={formLoading}
              >
                {formLoading ? (
                  <ActivityIndicator size="small" color={COLORS.primary.white} />
                ) : (
                  <PrimaryText fontSize={16} fontWeight={600} style={{ color: COLORS.primary.white }}>
                    Create Campaign
                  </PrimaryText>
                )}
              </TouchableOpacity>
            </View>
          </ScrollView>
        </FullModal>
      )}

      {/* Campaign Detail Modal */}
      {showDetailModal && selectedCampaign && (
        <FullModal setShowModal={() => setShowDetailModal(false)} title={selectedCampaign.name}>
          <ScrollView style={{ paddingHorizontal: isMobile ? 12 : 20 }}>
            {/* Status and Actions */}
            <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <View
                style={{
                  backgroundColor: isActive(selectedCampaign)
                    ? "#E8F5E9"
                    : isExpired(selectedCampaign)
                    ? "#FFEBEE"
                    : isNotStarted(selectedCampaign)
                    ? "#FFF3E0"
                    : "#F5F5F5",
                  paddingVertical: 6,
                  paddingHorizontal: 16,
                  borderRadius: 16,
                }}
              >
                <PrimaryText
                  fontSize={14}
                  fontWeight={600}
                  style={{
                    color: isActive(selectedCampaign)
                      ? "#2E7D32"
                      : isExpired(selectedCampaign)
                      ? "#C62828"
                      : isNotStarted(selectedCampaign)
                      ? "#EF6C00"
                      : "#757575",
                  }}
                >
                  {isActive(selectedCampaign) ? "Active" : isExpired(selectedCampaign) ? "Ended" : isNotStarted(selectedCampaign) ? "Upcoming" : "Inactive"}
                </PrimaryText>
              </View>
              <View style={{ flexDirection: "row", gap: 8 }}>
                <TouchableOpacity onPress={handleDetailToggleStatus} style={{ padding: 8, minHeight: 44, minWidth: 44, justifyContent: "center", alignItems: "center" }}>
                  <FaIcons.FaPowerOff size={18} color={isActive(selectedCampaign) ? COLORS.primary.error : COLORS.primary.blue} />
                </TouchableOpacity>
                <TouchableOpacity onPress={() => { setShowDetailModal(false); setShowQRModal(true); }} style={{ padding: 8, minHeight: 44, minWidth: 44, justifyContent: "center", alignItems: "center" }}>
                  <FaIcons.FaQrcode size={18} color={COLORS.primary.darkGray} />
                </TouchableOpacity>
                <TouchableOpacity onPress={handleDetailDelete} style={{ padding: 8, backgroundColor: "#FFEBEE", borderRadius: 18, minHeight: 44, minWidth: 44, justifyContent: "center", alignItems: "center" }}>
                  <FaIcons.FaTrash size={18} color={COLORS.primary.error} />
                </TouchableOpacity>
              </View>
            </View>

            {/* Stats Grid */}
            <View style={{ flexDirection: "row", flexWrap: "wrap", marginHorizontal: -8, marginBottom: 20 }}>
              <View style={{ width: "calc(50% - 16px)", margin: 8, backgroundColor: "#F8F9FA", borderRadius: 12, padding: 16 }}>
                <PrimaryText fontSize={14} style={{ color: COLORS.primary.gray }}>
                  Total Generated
                </PrimaryText>
                <PrimaryText fontSize={32} fontWeight={800} style={{ color: COLORS.primary.blue }}>
                  {campaignStats?.totalGenerated || 0}
                </PrimaryText>
              </View>
              <View style={{ width: "calc(50% - 16px)", margin: 8, backgroundColor: "#F8F9FA", borderRadius: 12, padding: 16 }}>
                <PrimaryText fontSize={14} style={{ color: COLORS.primary.gray }}>
                  Total Claimed
                </PrimaryText>
                <PrimaryText fontSize={32} fontWeight={800} style={{ color: "#2E7D32" }}>
                  {campaignStats?.totalClaimed || 0}
                </PrimaryText>
              </View>
              <View style={{ width: "calc(50% - 16px)", margin: 8, backgroundColor: "#F8F9FA", borderRadius: 12, padding: 16 }}>
                <PrimaryText fontSize={14} style={{ color: COLORS.primary.gray }}>
                  Pending Codes
                </PrimaryText>
                <PrimaryText fontSize={32} fontWeight={800} style={{ color: "#EF6C00" }}>
                  {campaignStats?.pending || 0}
                </PrimaryText>
              </View>
              <View style={{ width: "calc(50% - 16px)", margin: 8, backgroundColor: "#F8F9FA", borderRadius: 12, padding: 16 }}>
                <PrimaryText fontSize={14} style={{ color: COLORS.primary.gray }}>
                  Claim Rate
                </PrimaryText>
                <PrimaryText fontSize={32} fontWeight={800} style={{ color: "#9C27B0" }}>
                  {campaignStats?.claimRate || 0}%
                </PrimaryText>
              </View>
            </View>

            {/* Campaign Info Card */}
            <View style={{ backgroundColor: "#F8F9FA", borderRadius: 12, padding: 20, marginBottom: 20 }}>
              <PrimaryText fontSize={18} fontWeight={700} style={{ marginBottom: 16 }}>
                Campaign Information
              </PrimaryText>

              <View style={{ marginBottom: 12 }}>
                <PrimaryText fontSize={14} style={{ color: COLORS.primary.gray, marginBottom: 4 }}>
                  Campaign ID:
                </PrimaryText>
                <PrimaryText fontSize={14} fontWeight={500}>
                  {selectedCampaign.id}
                </PrimaryText>
              </View>

              <View style={{ marginBottom: 12 }}>
                <PrimaryText fontSize={14} style={{ color: COLORS.primary.gray, marginBottom: 4 }}>
                  Duration:
                </PrimaryText>
                <PrimaryText fontSize={14} fontWeight={500}>
                  {moment(selectedCampaign.startsAt).format("MMM DD, YYYY HH:mm")} -{" "}
                  {selectedCampaign.endsAt ? moment(selectedCampaign.endsAt).format("MMM DD, YYYY HH:mm") : "Ongoing"}
                </PrimaryText>
              </View>

              <View style={{ marginBottom: 12 }}>
                <PrimaryText fontSize={14} style={{ color: COLORS.primary.gray, marginBottom: 4 }}>
                  Created:
                </PrimaryText>
                <PrimaryText fontSize={14} fontWeight={500}>
                  {selectedCampaign.createdAt ? moment(selectedCampaign.createdAt).format("MMM DD, YYYY") : ""}
                </PrimaryText>
              </View>
            </View>

            {/* Store Info Card */}
            <View style={{ backgroundColor: "#F8F9FA", borderRadius: 12, padding: 20, marginBottom: 20 }}>
              <PrimaryText fontSize={18} fontWeight={700} style={{ marginBottom: 16 }}>
                Store Information
              </PrimaryText>

              <View style={{ marginBottom: 12 }}>
                <PrimaryText fontSize={14} style={{ color: COLORS.primary.gray, marginBottom: 4 }}>
                  Store Name:
                </PrimaryText>
                <PrimaryText fontSize={14} fontWeight={500}>
                  {selectedCampaign.storeName}
                </PrimaryText>
              </View>

              <View style={{ marginBottom: 12 }}>
                <PrimaryText fontSize={14} style={{ color: COLORS.primary.gray, marginBottom: 4 }}>
                  Address:
                </PrimaryText>
                <PrimaryText fontSize={14} fontWeight={500}>
                  {selectedCampaign.storeAddress}
                </PrimaryText>
              </View>
            </View>

            {/* Promotion Details Card */}
            <View style={{ backgroundColor: "#F8F9FA", borderRadius: 12, padding: 20, marginBottom: 20 }}>
              <PrimaryText fontSize={18} fontWeight={700} style={{ marginBottom: 16 }}>
                Promotion Details
              </PrimaryText>

              <View style={{ marginBottom: 12 }}>
                <PrimaryText fontSize={14} style={{ color: COLORS.primary.gray, marginBottom: 4 }}>
                  Static Code (show at store):
                </PrimaryText>
                <PrimaryText fontSize={18} fontWeight={700} style={{ color: COLORS.primary.blue }}>
                  {selectedCampaign.promoCode}
                </PrimaryText>
              </View>

              <View style={{ marginBottom: 12 }}>
                <PrimaryText fontSize={14} style={{ color: COLORS.primary.gray, marginBottom: 4 }}>
                  Label:
                </PrimaryText>
                <PrimaryText fontSize={14} fontWeight={500}>
                  {selectedCampaign.promoLabel}
                </PrimaryText>
              </View>

              {selectedCampaign.disclaimer && (
                <View style={{ backgroundColor: "#FFF3CD", borderLeftWidth: 3, borderLeftColor: "#FFC107", borderRadius: 6, padding: 12 }}>
                  <PrimaryText fontSize={13} style={{ color: "#856404" }}>
                    ⚠️ {selectedCampaign.disclaimer}
                  </PrimaryText>
                </View>
              )}
            </View>

            {/* Tab Switcher */}
            <View style={{ flexDirection: "row", borderBottomWidth: 1, borderBottomColor: COLORS.primary.lightGray, marginBottom: 16 }}>
              <TouchableOpacity
                onPress={() => setDetailView("overview")}
                style={[{ paddingVertical: 12, paddingHorizontal: 16, borderBottomWidth: 2 }, detailView === "overview" && { borderBottomColor: COLORS.primary.blue }]}
              >
                <PrimaryText fontSize={16} fontWeight={600} style={{ color: detailView === "overview" ? COLORS.primary.blue : COLORS.primary.gray }}>
                  Overview
                </PrimaryText>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => setDetailView("claims")}
                style={[{ paddingVertical: 12, paddingHorizontal: 16, borderBottomWidth: 2 }, detailView === "claims" && { borderBottomColor: COLORS.primary.blue }]}
              >
                <PrimaryText fontSize={16} fontWeight={600} style={{ color: detailView === "claims" ? COLORS.primary.blue : COLORS.primary.gray }}>
                  Claims ({campaignStats?.totalClaimed || 0})
                </PrimaryText>
              </TouchableOpacity>
            </View>

            {/* Recent Claims */}
            {detailView === "overview" && campaignStats?.recentClaims && campaignStats.recentClaims.length > 0 && (
              <View style={{ marginBottom: 20 }}>
                <PrimaryText fontSize={18} fontWeight={700} style={{ marginBottom: 12 }}>
                  Recent Claims
                </PrimaryText>
                {campaignStats.recentClaims.slice(0, 5).map((claim, index) => (
                  <View key={index} style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: "#E0E0E0" }}>
                    <View style={{ flex: 1 }}>
                      <PrimaryText fontSize={14} fontWeight={600}>
                        {claim.userName || "Unknown"}
                      </PrimaryText>
                      <PrimaryText fontSize={12} style={{ color: COLORS.primary.gray }}>
                        {claim.userEmail || ""}
                      </PrimaryText>
                    </View>
                    <PrimaryText fontSize={12} style={{ color: COLORS.primary.gray }}>
                      {claim.claimedAt ? moment(claim.claimedAt).fromNow() : ""}
                    </PrimaryText>
                  </View>
                ))}
              </View>
            )}

            {/* All Claims */}
            {detailView === "claims" && claims.length > 0 && (
              <View>
                <PrimaryText fontSize={18} fontWeight={700} style={{ marginBottom: 12 }}>
                  All Claims
                </PrimaryText>
                {claims.map((claim, index) => (
                  <View key={index} style={{ flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: "#E0E0E0", gap: isMobile ? 2 : 0 }}>
                    <View style={{ flex: isMobile ? undefined : 1 }}>
                      <PrimaryText fontSize={14} fontWeight={500}>
                        {claim.userName || "Unknown"}
                      </PrimaryText>
                      <PrimaryText fontSize={12} style={{ color: COLORS.primary.gray }}>
                        {claim.userEmail || ""}
                      </PrimaryText>
                    </View>
                    <PrimaryText fontSize={12} style={{ color: COLORS.primary.gray }}>
                      {claim.promoCodeId}
                    </PrimaryText>
                    <PrimaryText fontSize={12} style={{ color: COLORS.primary.gray }}>
                      {claim.claimedAt ? moment(claim.claimedAt).format("MMM DD, YYYY HH:mm") : ""}
                    </PrimaryText>
                  </View>
                ))}
              </View>
            )}

            {detailView === "claims" && claims.length === 0 && (
              <View style={{ paddingVertical: 60, alignItems: "center", justifyContent: "center" }}>
                <PrimaryText fontSize={16} style={{ color: COLORS.primary.gray }}>
                  No claims yet
                </PrimaryText>
              </View>
            )}
          </ScrollView>
        </FullModal>
      )}

      {/* Generate QR Modal */}
      <GenerateQRModal showModal={showQRModal} setShowModal={setShowQRModal} />
    </View>
  );
};

const styles = StyleSheet.create({
  tableContainer: {
    width: "100%",
  },
});
